import React from 'react';
import { ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon } from '@mui/icons-material';
import { QAItem } from '../types/qa';

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

export const getRatingIcon = (rating: string | null): React.ReactElement | null => {
  if (!rating) return null;
  const normalizedRating = rating.toUpperCase();
  if (normalizedRating === 'LIKED' || normalizedRating === 'POSITIVE') {
    return <ThumbUpIcon sx={{ color: '#2196f3', fontSize: 20 }} />;
  }
  if (normalizedRating === 'DISLIKED' || normalizedRating === 'NEGATIVE') {
    return <ThumbDownIcon sx={{ color: '#f44336', fontSize: 20 }} />;
  }
  return null;
};

/** True if the string is a standard UUID (e.g. spreadsheet row / correlation IDs). */
export const isUuidLike = (s: string | null | undefined): boolean => {
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
};

/** First CSV column: human question number when available, not the internal stream UUID. */
export const csvQuestionNumberCell = (
  answer: { id: string; question_number?: string },
  rowIndexZero: number
): string => {
  const qn = answer.question_number?.trim();
  if (qn && !isUuidLike(qn)) return cleanQuestionNumber(qn);
  return String(rowIndexZero + 1);
};

export const cleanQuestionNumber = (questionNumber: string): string => {
  if (!questionNumber) return questionNumber;

  // Support internal unique IDs like "sheet2:1@2" while keeping UI display clean ("1").
  // Strategy:
  // - take the portion after the last ":" (if present)
  // - then take the portion before "@" (if present)
  // - then prefer a leading number token, else return the cleaned token
  const afterColon = questionNumber.includes(':')
    ? questionNumber.slice(questionNumber.lastIndexOf(':') + 1)
    : questionNumber;
  const beforeAt = afterColon.includes('@') ? afterColon.split('@')[0] : afterColon;
  const token = beforeAt.trim();

  const match = token.match(/^(\d+)/);
  return match ? match[1] : token;
};

export const cleanQuestionText = (fullQuestion: string): string => {
  // Remove leading question number patterns like:
  // - "3. " or "3.3. " (dot notation)
  // - "1:", "1.", "1)" (various separators)
  // - Leading whitespace before numbers
  return fullQuestion
    // Strip UUID prefixes like "<uuid>:" or "<uuid>." (used internally for mapping)
    .replace(/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*[:.)-]\s*/i, '')
    .replace(/^\s*\d+\s*[:.)-]\s*/, '')  // Handle "1:", "1.", "1)", etc.
    .replace(/^\d+\.\s*\d*\.\s*/, '')    // Handle "3.3. " patterns
    .replace(/^\d+\.\s*/, '')             // Handle remaining "3. " patterns
    // Handle internal IDs that may be prefixed into stored full_question, e.g. "sheet2:1@2. Question..."
    .replace(/^\s*[^.\s]+:\s*[^.\s@]+@\d+\.\s*/, '');
};

/**
 * Escapes a value for CSV format.
 * Handles commas, quotes, and newlines by wrapping in quotes and escaping internal quotes.
 */
export const escapeCSVValue = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    // Escape double quotes by doubling them and wrap in quotes
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const getDisplayAnswer = (item: QAItem): string => {
  // Priority: admin_edited_ans > user_edited_ans > system_generated_ans > final_answer
  return item.admin_edited_ans || item.user_edited_ans || item.system_generated_ans || item.final_answer || '';
};

/**
 * Vertex RAG returns a vector distance on retrieved chunks (see vector_distance_threshold in retrieval).
 * Lower distance = closer match in embedding space. This is not a "confidence %" for the generated answer.
 */
export const formatRetrievalDistance = (score: number): string => {
  if (typeof score !== 'number' || Number.isNaN(score)) return '—';
  return score.toFixed(3);
};

/** Tooltip for per-chunk match score in retrieval UI. */
export const RETRIEVAL_DISTANCE_TOOLTIP =
  'OneSec compares your question to snippets from the knowledge base. This number is how far off the match is: ' +
  'smaller usually means that snippet lined up better with your wording.';

/** Tooltip for the average match line in the retrieval accordion header. */
export const AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP =
  'An average of the same match scores for the passages listed below. Lower usually means those snippets, taken together, were closer fits to your question—not that the generated answer is right or wrong.';

/** One-line context under retrieval sources (shown in expanded panel). */
export const RETRIEVAL_SCORE_LEGEND =
  'Strongest matches are listed first. Lower numbers mean a closer fit to your question.';

/** Average vector distance across sources, or null if none are numeric. */
export const averageRetrievalDistanceLabel = (
  sources: Array<{ relevance_score?: number }> | null | undefined
): string | null => {
  if (!sources?.length) return null;
  const nums = sources
    .map((s) => s.relevance_score)
    .filter((s): s is number => typeof s === 'number' && !Number.isNaN(s));
  if (nums.length === 0) return null;
  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  return formatRetrievalDistance(avg);
};

/** Best match first: ascending vector distance (missing scores last). */
export function sortRetrievalSourcesByDistance<T extends { relevance_score?: number | null }>(
  sources: T[] | null | undefined
): T[] {
  if (!sources?.length) return [];
  return [...sources].sort((a, b) => {
    const na =
      typeof a.relevance_score === 'number' && !Number.isNaN(a.relevance_score)
        ? a.relevance_score
        : Number.POSITIVE_INFINITY;
    const nb =
      typeof b.relevance_score === 'number' && !Number.isNaN(b.relevance_score)
        ? b.relevance_score
        : Number.POSITIVE_INFINITY;
    return na - nb;
  });
}

